import { useMutation } from '@apollo/client';
import ConfirmationModal from 'components/ConfirmationModal';
import { useAuth } from 'hooks/auth';
import { useInactivity } from 'hooks/useInactivity';
import { useTokenExpiry } from 'hooks/useTokenExpiry';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REFRESH_TOKEN } from 'services/auth';
import { useCountdown } from 'usehooks-ts';
import useToken, { getToken } from 'utils/token';

export type RefreshTokenModalProps = {
  isActive: boolean;
};

const RefreshTokenModal = ({ isActive }: RefreshTokenModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { setToken } = useToken();
  const [show, setShow] = useState<boolean>(false);

  const [count, { start, stop, reset }] = useCountdown({
    seconds: 60,
    interval: 1000,
  });

  const [refreshTokenMutation] = useMutation(REFRESH_TOKEN);

  // register the inactivity handler: after X minutes of inactivity, show the refresh-token-modal
  useInactivity({
    callback: () => setShow(true),
    isActive,
  });

  const refreshToken = useCallback(async () => {
    const { refreshToken } = getToken();
    const response = await refreshTokenMutation({
      variables: {
        refreshToken,
      },
    });
    setToken(response.data.refreshToken);
  }, [refreshTokenMutation, setToken]);

  // register the token-refresh handler: 1 minute before the token expires, show the refresh-token-modal
  useTokenExpiry({ isActive, onBeforeExpire: async () => await refreshToken() });

  const onResponse = useCallback(
    async (isConfirmed: boolean): Promise<void> => {
      if (isConfirmed) {
        // await refreshToken();
      } else {
        logout();
      }
      setShow(false);
    },
    [logout],
  );

  useEffect(() => {
    if (show) {
      reset();
      start();
    } else {
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!isNaN(count) && count < 1) {
      onResponse(false).catch();
      stop();
    }
  }, [count, onResponse, stop]);

  return (
    <ConfirmationModal
      show={show}
      cancelButtonTextKey={t('confirmation_logout')}
      text={t('confirmation_login_timeout', { count })}
      yesButtonTextKey={t('confirmation_stay_login')}
      onResponse={onResponse}
    />
  );
};

export default RefreshTokenModal;
